import React from 'react';
import { Link } from 'react-router-dom';
import {
    Workflow,
    BarChart3,
    Bot,
    Globe,
    ArrowRight,
    CheckCircle2,
    Zap,
    TrendingUp,
    Lightbulb,
    Layers,
    Code,
    Database
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernDigitalTransformation = () => {
    const services = [
        {
            icon: <Workflow size={28} />,
            title: 'Process Automation',
            description: 'Automate repetitive tasks and streamline workflows for maximum efficiency.',
            features: ['RPA implementation', 'Workflow design', 'Integration', 'Monitoring']
        },
        {
            icon: <BarChart3 size={28} />,
            title: 'Data Analytics',
            description: 'Transform raw data into actionable insights that drive business decisions.',
            features: ['BI dashboards', 'Predictive analytics', 'Data visualization', 'Custom reports']
        },
        {
            icon: <Bot size={28} />,
            title: 'AI Integration',
            description: 'Leverage artificial intelligence to enhance operations and customer experience.',
            features: ['Machine learning', 'NLP solutions', 'Chatbots', 'Intelligent automation']
        },
        {
            icon: <Globe size={28} />,
            title: 'Digital Services',
            description: 'Build modern digital experiences that engage customers and employees.',
            features: ['Web applications', 'Mobile apps', 'API development', 'User portals']
        },
        {
            icon: <Layers size={28} />,
            title: 'Cloud Migration',
            description: 'Move your operations to the cloud for improved scalability and cost efficiency.',
            features: ['Assessment', 'Migration planning', 'Execution', 'Optimization']
        },
        {
            icon: <Code size={28} />,
            title: 'Custom Development',
            description: 'Build tailored solutions that address your unique business challenges.',
            features: ['Full-stack dev', 'API integration', 'Legacy modernization', 'DevOps']
        }
    ];

    const outcomes = [
        {
            icon: <Zap size={28} />,
            title: 'Increased Efficiency',
            description: 'Automated processes reduce manual work and accelerate operations'
        },
        {
            icon: <TrendingUp size={28} />,
            title: 'Data-Driven Decisions',
            description: 'Make informed choices backed by real-time analytics and insights'
        },
        {
            icon: <Lightbulb size={28} />,
            title: 'Innovation Ready',
            description: 'Modern infrastructure that supports continuous innovation'
        }
    ];

    const features = [
        { icon: <Bot size={24} />, title: 'AI-Powered', text: 'Smart automation' },
        { icon: <Database size={24} />, title: 'Data-Driven', text: 'Actionable insights' },
        { icon: <Globe size={24} />, title: 'Digital-First', text: 'Modern experiences' },
        { icon: <Zap size={24} />, title: 'Agile', text: 'Rapid delivery' }
    ];

    const stats = [
        { number: '40%', label: 'Efficiency Increase' },
        { number: '60%', label: 'Cost Reduction' },
        { number: '3x', label: 'Faster Deployment' },
        { number: '99%', label: 'Client Satisfaction' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Digital Transformation"
                subtitle="Innovate, Transform, and Lead in the Digital Age"
                breadcrumbs={[
                    { label: 'Solutions', path: '/solutions' },
                    { label: 'Digital Transformation' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Workflow size={16} />
                                Transform Your Business
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Implementing Innovative Technologies to
                                <span className="highlight"> Optimize Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We help organizations embrace digital transformation through process
                                automation, data analytics, AI integration, and modern digital services.
                                Our solutions drive efficiency, innovation, and competitive advantage.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Start Your Journey
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    Explore Solutions
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="jk-solutions-stats">
                <div className="jk-solutions-stats__container">
                    <div className="jk-solutions-stats__grid">
                        {stats.map((stat, index) => (
                            <div key={index} className="jk-solutions-stats__item">
                                <div className="jk-solutions-stats__number">{stat.number}</div>
                                <div className="jk-solutions-stats__label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Layers size={16} />
                            Our Services
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Digital Transformation
                            <span className="highlight"> Solutions</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            End-to-end digital solutions that drive innovation and business growth
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-solutions-services__card-title">{service.title}</h3>
                                <p className="jk-solutions-services__card-description">{service.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes Section */}
            <section className="jk-solutions-outcomes">
                <div className="jk-solutions-outcomes__container">
                    <div className="jk-solutions-outcomes__header">
                        <h2 className="jk-solutions-outcomes__title">
                            Transformation Outcomes
                        </h2>
                    </div>

                    <div className="jk-solutions-outcomes__grid">
                        {outcomes.map((outcome, index) => (
                            <div key={index} className="jk-solutions-outcomes__card">
                                <div className="jk-solutions-outcomes__card-icon">
                                    {outcome.icon}
                                </div>
                                <h3 className="jk-solutions-outcomes__card-title">{outcome.title}</h3>
                                <p className="jk-solutions-outcomes__card-text">{outcome.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernDigitalTransformation;
